/**
 * Capacitor bridge for native Face ID / fingerprint login.
 * The remote WebView has no npm wrapper, so this calls
 * window.Capacitor.Plugins.NativeBiometric directly.
 * Never store or log the password.
 *
 * Credentials are keyed by hostname, the same key the PHP login and Profile
 * switch use, so an enrollment on either page works on the other.
 */

export const NATIVE_BIOMETRIC_LOGIN_FLAG = "native-biometric-login";

export const STORAGE_KEYS = {
  ownerEmail: "hw_biometric_owner_email",
  optInDeclined: "hw_biometric_opt_in_declined",
  skipAutoPrompt: "hw_biometric_skip_auto_prompt",
} as const;

export const BRIDGE_ATTEMPTS = 15;
export const BRIDGE_DELAY_MS = 300;

const BIOMETRY_LABELS: Record<number, string> = {
  1: "Touch ID",
  2: "Face ID",
  3: "Fingerprint",
  4: "Face Unlock",
};

export const VERIFY_IDENTITY_OPTIONS = {
  reason: "Sign in to Homeworks",
  title: "Sign in",
  subtitle: "",
  description: "",
  negativeButtonText: "Cancel",
  useFallback: false,
} as const;

export const PASSWORD_CHANGED_MESSAGE =
  "Your password was changed. Please sign in with email and password to re-enable Face ID.";

export type BiometricAvailability = {
  usable: boolean;
  label: string;
  saved: boolean;
  ownerEmail: string;
  occupied: boolean;
};

export type BiometricCredentials = {
  username: string;
  password: string;
};

type NativeBiometricPlugin = NonNullable<
  NonNullable<Window["Capacitor"]>["Plugins"]
>["NativeBiometric"];

let availabilityPromise: Promise<BiometricAvailability> | null = null;
let availabilityFlag: boolean | null = null;

function getCapacitor(): Window["Capacitor"] | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window.Capacitor ?? null;
}

export function getPlugin(): NativeBiometricPlugin | null {
  const capacitor = getCapacitor();
  return capacitor?.Plugins?.NativeBiometric ?? null;
}

export function isNativePlatform(): boolean {
  const capacitor = getCapacitor();
  if (!capacitor) {
    return false;
  }
  if (typeof capacitor.isNativePlatform === "function") {
    return Boolean(capacitor.isNativePlatform());
  }
  const platform =
    typeof capacitor.getPlatform === "function"
      ? capacitor.getPlatform()
      : null;
  return platform === "ios" || platform === "android";
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Poll for the native bridge. Browsers (no Capacitor after the document is ready)
 * stop early so a flag-on website does not stall login. Native shells keep polling
 * until the plugin appears or the full 15 x 300ms budget is spent.
 */
export async function waitForPlugin(): Promise<NativeBiometricPlugin | null> {
  for (let attempt = 0; attempt < BRIDGE_ATTEMPTS; attempt += 1) {
    if (isNativePlatform() && getPlugin()) {
      return getPlugin();
    }
    if (getCapacitor() && !isNativePlatform()) {
      return null;
    }
    const bridgeMissing = !getCapacitor();
    const documentDone =
      typeof document === "undefined" ||
      document.readyState === "complete" ||
      document.readyState === "interactive";
    if (bridgeMissing && documentDone && attempt > 0) {
      return null;
    }
    await delay(BRIDGE_DELAY_MS);
  }
  return isNativePlatform() && getPlugin() ? getPlugin() : null;
}

export function getServerKey(): string {
  if (typeof window === "undefined" || !window.location) {
    return "";
  }
  return window.location.hostname || "";
}

export function redirectToAppHome(): void {
  window.location.replace(process.env.NEXT_PUBLIC_BASE_URL ?? "");
}

export function invalidateAvailability(): void {
  availabilityPromise = null;
  availabilityFlag = null;
}

export function normalizeEmail(email: string | null | undefined): string {
  return String(email || "")
    .trim()
    .toLowerCase();
}

function readStorage(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Private mode or a blocked storage API. Face ID UI will just stay off.
  }
}

function removeStorage(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage failures. The Keychain wipe is the part that matters.
  }
}

export function getOwnerEmail(): string {
  if (typeof localStorage === "undefined") {
    return "";
  }
  return normalizeEmail(readStorage(localStorage, STORAGE_KEYS.ownerEmail));
}

export function setOwnerEmail(email: string): void {
  const normalized = normalizeEmail(email);
  if (!normalized || typeof localStorage === "undefined") {
    return;
  }
  writeStorage(localStorage, STORAGE_KEYS.ownerEmail, normalized);
}

export function clearOwnerEmail(): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  removeStorage(localStorage, STORAGE_KEYS.ownerEmail);
}

export function isOptInDeclined(): boolean {
  if (typeof localStorage === "undefined") {
    return false;
  }
  return readStorage(localStorage, STORAGE_KEYS.optInDeclined) === "true";
}

export function setOptInDeclined(): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  writeStorage(localStorage, STORAGE_KEYS.optInDeclined, "true");
}

export function clearOptInDeclined(): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  removeStorage(localStorage, STORAGE_KEYS.optInDeclined);
}

export function shouldSkipAutoPrompt(): boolean {
  if (typeof sessionStorage === "undefined") {
    return false;
  }
  return readStorage(sessionStorage, STORAGE_KEYS.skipAutoPrompt) === "true";
}

export function setSkipAutoPrompt(): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  writeStorage(sessionStorage, STORAGE_KEYS.skipAutoPrompt, "true");
}

export function labelFromBiometryType(biometryType: unknown): string {
  const label = BIOMETRY_LABELS[Number(biometryType)];
  return label || "Biometrics";
}

function readFlag(result: unknown, key: string): boolean {
  if (result && typeof result === "object" && key in result) {
    return Boolean((result as Record<string, unknown>)[key]);
  }
  return Boolean(result);
}

function isUserCancel(error: unknown): boolean {
  const record =
    error && typeof error === "object"
      ? (error as {
          code?: unknown;
          errorCode?: unknown;
          message?: unknown;
          errorMessage?: unknown;
        })
      : null;
  const code = record ? (record.code ?? record.errorCode) : undefined;
  const message = String(
    (record && (record.message || record.errorMessage)) || error || "",
  ).toLowerCase();
  if (
    code === 13 ||
    code === 16 ||
    code === "userCancel" ||
    code === "user_cancel"
  ) {
    return true;
  }
  return message.indexOf("cancel") !== -1;
}

async function checkHardware(): Promise<{ available: boolean; label: string }> {
  const plugin = getPlugin();
  if (!plugin || typeof plugin.isAvailable !== "function") {
    return { available: false, label: "Biometrics" };
  }
  try {
    const result = await plugin.isAvailable({ useFallback: false });
    return {
      available: readFlag(result, "isAvailable"),
      label: labelFromBiometryType(
        result && typeof result === "object" && "biometryType" in result
          ? result.biometryType
          : undefined,
      ),
    };
  } catch {
    console.warn("biometric_login: availability check failed");
    return { available: false, label: "Biometrics" };
  }
}

export async function isCredentialsSaved(): Promise<boolean> {
  const plugin = getPlugin();
  if (
    !getServerKey() ||
    !plugin ||
    typeof plugin.isCredentialsSaved !== "function"
  ) {
    return false;
  }
  try {
    const result = await plugin.isCredentialsSaved({ server: getServerKey() });
    return readFlag(result, "isSaved");
  } catch {
    console.warn("biometric_login: saved-credential check failed");
    return false;
  }
}

export async function readAvailability(
  flagEnabled: boolean,
): Promise<BiometricAvailability> {
  const enabled = Boolean(flagEnabled);
  if (availabilityPromise && availabilityFlag === enabled) {
    return availabilityPromise;
  }
  availabilityFlag = enabled;
  const request = computeAvailability(enabled);
  availabilityPromise = request;
  request.catch(() => {
    if (availabilityPromise === request) {
      invalidateAvailability();
    }
  });
  return request;
}

async function computeAvailability(
  flagEnabled: boolean,
): Promise<BiometricAvailability> {
  const empty: BiometricAvailability = {
    usable: false,
    label: "Biometrics",
    saved: false,
    ownerEmail: getOwnerEmail(),
    occupied: false,
  };
  if (!flagEnabled || !getServerKey()) {
    return empty;
  }
  const plugin = await waitForPlugin();
  if (!plugin) {
    return empty;
  }
  const hardware = await checkHardware();
  if (!hardware.available) {
    return {
      usable: false,
      label: hardware.label,
      saved: false,
      ownerEmail: getOwnerEmail(),
      occupied: false,
    };
  }
  const saved = await isCredentialsSaved();
  const ownerEmail = getOwnerEmail();
  return {
    usable: true,
    label: hardware.label,
    saved,
    ownerEmail,
    occupied: saved && !ownerEmail,
  };
}

export function canEnableForEmail(
  state: BiometricAvailability | null | undefined,
  email: string,
): boolean {
  if (!state || !state.usable) {
    return false;
  }
  if (!state.saved) {
    return true;
  }
  const ownerEmail = normalizeEmail(state.ownerEmail);
  return ownerEmail !== "" && ownerEmail === normalizeEmail(email);
}

export async function verifyIdentity(): Promise<{
  ok: boolean;
  cancelled: boolean;
}> {
  const plugin = getPlugin();
  if (!plugin || typeof plugin.verifyIdentity !== "function") {
    return { ok: false, cancelled: false };
  }
  try {
    const result = await plugin.verifyIdentity(VERIFY_IDENTITY_OPTIONS);
    if (
      result &&
      typeof result === "object" &&
      (result.isVerified === false || result.verified === false)
    ) {
      return { ok: false, cancelled: isUserCancel(result) };
    }
    return { ok: true, cancelled: false };
  } catch (error) {
    return { ok: false, cancelled: isUserCancel(error) };
  }
}

export async function getCredentials(): Promise<BiometricCredentials | null> {
  const plugin = getPlugin();
  if (
    !getServerKey() ||
    !plugin ||
    typeof plugin.getCredentials !== "function"
  ) {
    return null;
  }
  try {
    const result = await plugin.getCredentials({ server: getServerKey() });
    if (!result || typeof result !== "object") {
      return null;
    }
    const username = result.username == null ? "" : String(result.username);
    const password = result.password == null ? "" : String(result.password);
    if (!username || !password) {
      return null;
    }
    return { username, password };
  } catch {
    console.warn("biometric_login: credential read failed");
    return null;
  }
}

export async function setCredentials(
  username: string,
  password: string,
): Promise<void> {
  const plugin = getPlugin();
  const server = getServerKey();
  if (!server || !plugin || typeof plugin.setCredentials !== "function") {
    throw new Error("biometric_unavailable");
  }
  await plugin.setCredentials({
    username: String(username).trim(),
    password: String(password),
    server,
  });
  setOwnerEmail(username);
  clearOptInDeclined();
  invalidateAvailability();
}

export async function deleteCredentials(): Promise<boolean> {
  const plugin = getPlugin();
  const server = getServerKey();
  if (!server || !plugin || typeof plugin.deleteCredentials !== "function") {
    return false;
  }
  try {
    await plugin.deleteCredentials({ server });
    return true;
  } catch {
    console.warn("biometric_login: credential delete failed");
    return false;
  }
}

/**
 * Remove the saved login. Local owner/declined flags change only after the
 * Keychain no longer has credentials, so a failed delete cannot hide the switch
 * while the old password is still on the device.
 */
export async function wipeSavedLogin(markDeclined: boolean): Promise<boolean> {
  await deleteCredentials();
  const stillSaved =
    isNativePlatform() && getPlugin() ? await isCredentialsSaved() : false;
  if (stillSaved) {
    return false;
  }
  clearOwnerEmail();
  if (markDeclined) {
    setOptInDeclined();
  }
  invalidateAvailability();
  return true;
}

export function passwordChangeShouldWipe(
  input:
    | {
        currentUserId?: string;
        employeeId?: string;
        employeeEmail?: string;
        ownerEmail?: string;
        hasSavedLogin?: boolean;
      }
    | null
    | undefined,
): boolean {
  const currentUserId = String(input?.currentUserId || "").trim();
  const employeeId = String(input?.employeeId || "").trim();
  const employeeEmail = normalizeEmail(input?.employeeEmail);
  const ownerEmail = normalizeEmail(input?.ownerEmail);
  const hasSavedLogin = Boolean(input?.hasSavedLogin) || ownerEmail !== "";
  if (!hasSavedLogin) {
    return false;
  }
  if (
    currentUserId !== "" &&
    employeeId !== "" &&
    currentUserId === employeeId
  ) {
    return true;
  }
  return (
    ownerEmail !== "" && employeeEmail !== "" && ownerEmail === employeeEmail
  );
}
